import React, { useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { reduxForm, formValueSelector } from 'redux-form'
import Tabs from 'components/tabs'
import Dropdown from 'components/dropdown'
import { Tab } from '@cred/react-tabs'
import { useViewport } from 'components/hooks'
import { tabClick } from '@cred/react-analytics/events'
import { withSnowplowContexts } from '@cred/react-analytics/SnowplowContextManager'
import InitialValuesContext from 'components/initial-values-context'
import PersonalLoanRateTable from '../../personal'
import StudentLoanRateTable from '../../student-loan'
import StudentLoanRefiRateTable from '../../student-loan-refi'
import CreditCardsRateTable from '../../credit-cards'
import MortgageRateTable from '../../mortgage'
import HYSRateTable from '../../high-yield-savings'
import styles from './styles.module.scss'

const verticalsTabMapping = {
    'personal-loan': {
      tabName: 'Personal Loan',
      tabContent: PersonalLoanRateTable,
    },
    'student-loan': {
      tabName: 'Student Loan',
      tabContent: StudentLoanRateTable,
    },
    'student-loan-refi': {
      tabName: 'Student Loan Refi',
      tabContent: StudentLoanRefiRateTable,
    },
    'credit-cards': {
      tabName: 'Credit Cards',
      tabContent: CreditCardsRateTable,
    },
    mortgage: {
      tabName: 'Mortgage',
      tabContent: MortgageRateTable,
    },
    'high-yield-savings': {
      tabName: 'Savings',
      tabContent: HYSRateTable,
    },
  },
  formName = 'vertical-tabs-form',
  partners = ['credible', 'yahoo1'],

  getValidVerticals = (verticals) => {
    const validVerticals = Object.keys(verticalsTabMapping)

    return verticals.filter((vertical) => validVerticals.includes(vertical))
  },

  getTabName = (vertical) => verticalsTabMapping[vertical].tabName,

  getTabContent = (vertical, props) => {
    const TabContentComponent = verticalsTabMapping[vertical].tabContent

    return <TabContentComponent {...props} />
  },

  getOptions = (verticals) => {
    const options = []
    verticals.forEach(vertical => {
      options.push({ value: vertical, label: getTabName(vertical) })
    })
    return options
  },

  getLabel = (vertical) => (vertical !== 'credit-cards') ? 'Compare rates for...' : 'I\'m looking for',

  onTabClicked = (validVerticals, setVertical, trackSnowplowEvent, change) => (index) => {
    const vertical = validVerticals[index]

    setVertical(vertical)
    change('vertical', vertical)
    trackSnowplowEvent(tabClick, { text: vertical })
  },

  VerticalTabsSection = ({ vertical, verticals, initialValues, setVertical, theme, trackSnowplowEvent, change, ...props }) => {
    const { initialValues: partnerFormData } = useContext(InitialValuesContext)
    const validVerticals = getValidVerticals(verticals),
      verticalOption = vertical || initialValues.vertical,
      tabTheme = partners.includes(theme) ? theme : '',
      isMobile = useViewport() < 568

    useEffect(() => {
      setVertical(verticalOption)
    }, [verticalOption])

    return (
      <div className={[styles.tabsContainer, styles[tabTheme]].join(' ')}>
        <section className="hidden-xs">
          <Tabs
            type="flat"
            activeTab={validVerticals.indexOf(verticalOption)}
            onTabClick={onTabClicked(validVerticals, setVertical, trackSnowplowEvent, change)}
            branding={{
              tabHeader: 'tab-header',
              tabContent: 'tab-content',
              tabItem: 'tab-item',
              tabItemWrapper: 'tab-item-wrapper',
              tabActive: 'tab-active',
            }}
          >
            { validVerticals.map(validVertical => (
              <Tab key={validVertical} title={getTabName(validVertical)}>
                { !isMobile && getTabContent(validVertical, { ...props, initialValues: partnerFormData }) }
              </Tab>
            ))}
          </Tabs>
        </section>
        <section className={`${styles.mobileDropdown} hidden-sm hidden-md hidden-lg hidden-xl`}>
          <Dropdown
            name="vertical"
            label={getLabel(verticalOption)}
            placeholder="Select a product"
            options={getOptions(validVerticals)}
            icon={false}
          />
          <div className="mt-8">
            { isMobile && getTabContent(verticalOption, { ...props, initialValues: partnerFormData }) }
          </div>
        </section>
      </div>
    )
  },

  selector = formValueSelector(formName),
  mapStateToProps = (state, { verticals }) => {
    const initialValues = {
      vertical: getValidVerticals(verticals)[0],
    }

    return {
      vertical: selector(state, 'vertical'),
      initialValues,
    }
  }

VerticalTabsSection.propTypes = {
  vertical: PropTypes.string,
  setVertical: PropTypes.func,
  initialValues: PropTypes.object,
  verticals: PropTypes.array.isRequired,
  theme: PropTypes.string,
  trackSnowplowEvent: PropTypes.func,
  change: PropTypes.func,
}

export { VerticalTabsSection, getValidVerticals, getTabName, getTabContent, getOptions, getLabel, onTabClicked, mapStateToProps }

export default compose(
  connect(mapStateToProps),
  withSnowplowContexts,
  reduxForm({
    form: formName,
    keepDirtyOnReinitialize: true,
  }),
)(VerticalTabsSection)
